import { Button, FormButtonRow } from '@ksyos/design-system';
import { useState } from 'react';
import type { HolterDiaryUploadData } from '@common/validation/customControls/cardiology/holter/holterDiaryUploadSchemas';
import { CustomControlCode, DiaryTypes, ProcessActivityStatus, ValidationSchemaVersion } from '@common/enums';
import { createProcessActivityContextData } from '@common/utils/serviceContextUtils';
import { sleep } from '@common/utils/sleep';
import { getCustomControlValidationSchemaContainer } from '@common/workflow/serviceActivityRegistration';
import { PatientEpisodeCategoryCarePathContext } from '@frontend/components/pages/episodeCategory/carePath/patientEpisodeCategoryCarePathContext';
import { ProcessActivityEditCancelButton } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityEdit/ProcessActivityEditCancelButton';
import { ProcessContext } from '@frontend/components/pages/episodeCategory/carePath/process/processContext';
import { PatientEpisodeCategoryContext } from '@frontend/components/pages/episodeCategory/patientEpisodeCategoryContext';
import { getOneProcessActivity } from '@frontend/services/api/processActivitiesApiService';
import { createHolterDiaryForProcess } from '@frontend/services/api/processesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import {
    useProcessActivityContext,
    useProcessActivityEditContext,
} from '@frontend/utils/hooks/useProcessActivityContext';
import { validate } from '@frontend/utils/validation';
import { scrollToRefValidationError } from '@frontend/utils/validationScrollUtils';

export function HolterDiaryUploadButton(): JSX.Element {
    const abortSignal = useAbortSignal();
    const { data, formRef, onChange } = useProcessActivityEditContext<HolterDiaryUploadData>();
    const { process } = useDefinedContext(ProcessContext);
    const { reload, setSaveStatus } = useDefinedContext(PatientEpisodeCategoryContext);
    const { patientData, processData } = useProcessActivityContext();
    const { isSavingAndReloading, setActiveProcessAction } = useDefinedContext(PatientEpisodeCategoryCarePathContext);

    const [isSaving, setIsSaving] = useState(false);

    return (
        <FormButtonRow>
            <ProcessActivityEditCancelButton isDisabled={isSavingAndReloading || isSaving} />
            <Button
                isDisabled={isSavingAndReloading || isSaving}
                onClick={async () => await saveDiaryData()}
                variant="solid"
            >
                Bewaar
            </Button>
        </FormButtonRow>
    );

    async function saveDiaryData() {
        setIsSaving(true);
        onChange({ ...data, error: null });
        const hasErrors = await onValidate();
        if (hasErrors) {
            setIsSaving(false);

            return;
        }

        setSaveStatus('saving');

        const processActivityId = await createHolterDiaryForProcess(process.id, data);

        if (data.diaryType === DiaryTypes.paperUpload) {
            await waitForDiaryCreation(processActivityId);
        } else {
            onSuccessHandler();
        }
    }

    async function waitForDiaryCreation(processActivityId: string) {
        const intervalMilliseconds = 1000;
        const maxWaitTimeMilliseconds = 5 * 60 * 1000;
        let currentWaitTimeMilliseconds = 0;
        while (currentWaitTimeMilliseconds < maxWaitTimeMilliseconds) {
            await sleep(intervalMilliseconds);
            currentWaitTimeMilliseconds += intervalMilliseconds;

            const processActivity = await getOneProcessActivity(processActivityId, abortSignal);

            if (processActivity?.customControlData?.error) {
                onChange(processActivity.customControlData);
                setIsSaving(false);
                setSaveStatus(undefined);

                return;
            }

            if (processActivity.status === ProcessActivityStatus.complete) {
                onSuccessHandler();

                return;
            }
        }

        setIsSaving(false);
    }

    function onSuccessHandler() {
        setIsSaving(false);
        setSaveStatus('saved');
        setActiveProcessAction(undefined);
        reload();
    }

    async function onValidate() {
        const { hasErrors } = validate(
            getCustomControlValidationSchemaContainer(
                CustomControlCode.HolterDiaryUpload,
                ValidationSchemaVersion.final,
                1
            ),
            createProcessActivityContextData({
                customControlData: data,
                patientData,
                processData,
            })
        );

        if (hasErrors) {
            const validateKeys = Object.keys(data);
            onChange(data, validateKeys);
        }

        scrollToRefValidationError(formRef);

        return hasErrors;
    }
}
